// Freehand stroke geometry (spec §5.5). Points are stored relative to the
// object's x/y. Resizing changes only w/h: points are scaled at draw and hit
// time, so a resize is a tiny op rather than a rewrite of every point.
import type { Freehand } from '../../shared/types.ts';
import { distToSegmentSq } from '../util/geometry.ts';
import { simplify } from '../util/simplify.ts';

export const MAX_STROKE_POINTS = 5000; // spec §7.2

interface Extents { minX: number; minY: number; maxX: number; maxY: number }
const extentsCache = new WeakMap<readonly number[], Extents>();

function extents(points: readonly number[]): Extents {
  let e = extentsCache.get(points);
  if (e) return e;
  e = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
  for (let i = 0; i < points.length; i += 2) {
    e.minX = Math.min(e.minX, points[i]!);
    e.maxX = Math.max(e.maxX, points[i]!);
    e.minY = Math.min(e.minY, points[i + 1]!);
    e.maxY = Math.max(e.maxY, points[i + 1]!);
  }
  extentsCache.set(points, e);
  return e;
}

/** Maps stored points into the object's current local box. */
export interface StrokeFrame { minX: number; minY: number; kx: number; ky: number; pad: number }

export function strokeFrame(o: Freehand): StrokeFrame {
  const e = extents(o.points);
  const pad = o.strokeWidth / 2;
  const rx = e.maxX - e.minX, ry = e.maxY - e.minY;
  return {
    minX: e.minX, minY: e.minY, pad,
    kx: rx > 1e-9 ? Math.max(0, o.w - 2 * pad) / rx : 1,
    ky: ry > 1e-9 ? Math.max(0, o.h - 2 * pad) / ry : 1,
  };
}

/** The i-th stroke point in object-local coords. */
export function strokePoint(f: StrokeFrame, points: readonly number[], i: number): [number, number] {
  return [f.pad + (points[i * 2]! - f.minX) * f.kx, f.pad + (points[i * 2 + 1]! - f.minY) * f.ky];
}

/** Whether object-local (lx, ly) is within `tol` of the stroke. */
export function hitStroke(o: Freehand, lx: number, ly: number, tol: number): boolean {
  const f = strokeFrame(o);
  const r = o.strokeWidth / 2 + tol;
  const r2 = r * r;
  const n = o.points.length >> 1;
  let [px, py] = strokePoint(f, o.points, 0);
  if (n === 1) return (lx - px) ** 2 + (ly - py) ** 2 <= r2;
  for (let i = 1; i < n; i++) {
    const [qx, qy] = strokePoint(f, o.points, i);
    if (distToSegmentSq(lx, ly, px, py, qx, qy) <= r2) return true;
    px = qx;
    py = qy;
  }
  return false;
}

/**
 * Turns raw world-space points into stroke geometry: simplified (tighter
 * tolerance first, looser if needed to fit the point limit), positioned so the
 * box includes the stroke width.
 */
export function buildStroke(raw: readonly number[], tolerance: number, strokeWidth: number):
  { x: number; y: number; w: number; h: number; points: number[] } {
  let pts = simplify(raw, tolerance);
  for (let t = tolerance * 2; pts.length > MAX_STROKE_POINTS * 2; t *= 2) pts = simplify(raw, t);
  const e = extents(pts);
  const pad = strokeWidth / 2;
  const x = e.minX - pad, y = e.minY - pad;
  const points = pts.map((v, i) => Math.round(((i & 1 ? v - y : v - x)) * 100) / 100);
  return { x, y, w: e.maxX - e.minX + strokeWidth, h: e.maxY - e.minY + strokeWidth, points };
}

/**
 * The stroke as cubic Béziers through its points (Catmull-Rom, spec §5.5), in
 * object-local coords: shared by the canvas renderer and SVG export.
 */
export function strokeCurve(o: Freehand): { start: [number, number]; curves: [number, number, number, number, number, number][] } {
  const f = strokeFrame(o);
  const n = o.points.length >> 1;
  const p = (i: number) => strokePoint(f, o.points, Math.max(0, Math.min(n - 1, i)));
  const curves: [number, number, number, number, number, number][] = [];
  for (let i = 0; i < n - 1; i++) {
    const [ax, ay] = p(i - 1), [bx, by] = p(i), [cx, cy] = p(i + 1), [dx, dy] = p(i + 2);
    curves.push([bx + (cx - ax) / 6, by + (cy - ay) / 6, cx - (dx - bx) / 6, cy - (dy - by) / 6, cx, cy]);
  }
  return { start: p(0), curves };
}
